import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { bloodBankAPI } from '../../utils/api';
import { toast } from 'react-toastify';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const GROUP_COLORS = { 'A+': '#dc2626', 'A-': '#b91c1c', 'B+': '#2563eb', 'B-': '#1d4ed8', 'AB+': '#7c3aed', 'AB-': '#6d28d9', 'O+': '#059669', 'O-': '#047857' };

export default function BloodBankIssue() {
    const navigate = useNavigate();
    const location = useLocation();
    const preRequest = location.state?.request;

    const [requests, setRequests] = useState([]);
    const [selectedRequest, setSelectedRequest] = useState(preRequest || null);
    const [inventory, setInventory] = useState([]);
    const [selectedBags, setSelectedBags] = useState([]);
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [issued, setIssued] = useState(null);

    useEffect(() => {
        bloodBankAPI.getRequests({ status: 'Pending' }).then(r => setRequests(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (selectedRequest) {
            bloodBankAPI.getInventory({ blood_group: selectedRequest.blood_group, component: selectedRequest.component })
                .then(r => setInventory(r.data)).catch(() => {});
            setSelectedBags([]);
        }
    }, [selectedRequest]);

    const toggleBag = (bag) => {
        setSelectedBags(prev => {
            const exists = prev.find(b => b.id === bag.id);
            if (exists) return prev.filter(b => b.id !== bag.id);
            const needed = selectedRequest.units_requested - (selectedRequest.units_issued || 0);
            if (prev.length >= needed) { toast.warning(`Only ${needed} unit(s) needed`); return prev; }
            return [...prev, bag];
        });
    };

    const handleIssue = async () => {
        if (!selectedRequest) return toast.error('Select a request');
        if (selectedBags.length === 0) return toast.error('Select at least one bag');
        setLoading(true);
        try {
            const res = await bloodBankAPI.issueBlood({
                request_id: selectedRequest.id,
                bag_ids: selectedBags.map(b => b.id),
                notes,
            });
            toast.success(`${res.data.issues?.length} unit(s) issued successfully`);
            setIssued(res.data);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Issue failed');
        } finally { setLoading(false); }
    };

    if (issued) {
        return (
            <div style={{ maxWidth: 500, margin: '60px auto', textAlign: 'center' }}>
                <div style={{ fontSize: 60, marginBottom: 16 }}>✅</div>
                <h2 style={{ fontSize: 22, color: '#059669' }}>Blood Issued Successfully</h2>
                <p style={{ fontSize: 14, color: '#64748b' }}>{issued.issues?.length} unit(s) issued to {selectedRequest.patient_name}</p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 24 }}>
                    <button onClick={() => { setIssued(null); setSelectedRequest(null); setSelectedBags([]); setNotes(''); }}
                        style={{ padding: '10px 24px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        Issue More
                    </button>
                    <button onClick={() => navigate('/blood-bank')}
                        style={{ padding: '10px 24px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <button onClick={() => navigate('/blood-bank')} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13 }}>← Back</button>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e3a5f', margin: 0 }}>💉 Issue Blood</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Select request and bags to issue</p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                {/* Left — Select Request */}
                <div>
                    <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: 18, marginBottom: 16 }}>
                        <h4 style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', margin: '0 0 12px' }}>Select Blood Request</h4>
                        {requests.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 13 }}>No pending requests</div>
                        ) : requests.map(r => (
                            <div key={r.id} onClick={() => setSelectedRequest(r)}
                                style={{ padding: '12px 14px', borderRadius: 8, border: `2px solid ${selectedRequest?.id === r.id ? '#dc2626' : '#e2e8f0'}`, marginBottom: 8, cursor: 'pointer', background: selectedRequest?.id === r.id ? '#fef2f2' : 'white' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontWeight: 700, fontSize: 13 }}>{r.patient_name}</span>
                                    <span style={{ fontWeight: 900, fontSize: 15, color: GROUP_COLORS[r.blood_group] }}>{r.blood_group}</span>
                                </div>
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                    {r.request_number} · {r.units_requested} unit(s) · {r.component} · <span style={{ color: r.priority === 'Emergency' ? '#dc2626' : r.priority === 'Urgent' ? '#d97706' : '#059669', fontWeight: 700 }}>{r.priority}</span>
                                </div>
                                {r.ward && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Ward: {r.ward} | Dr. {r.doctor_name}</div>}
                            </div>
                        ))}
                    </div>

                    {selectedRequest && (
                        <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: 18 }}>
                            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', margin: '0 0 12px' }}>Notes</h4>
                            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Additional notes..."
                                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'vertical', minHeight: 80, boxSizing: 'border-box' }} />
                        </div>
                    )}
                </div>

                {/* Right — Available Bags */}
                <div>
                    {selectedRequest && (
                        <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: 18 }}>
                            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', margin: '0 0 4px' }}>
                                Available Bags — <span style={{ color: GROUP_COLORS[selectedRequest.blood_group] }}>{selectedRequest.blood_group}</span>
                            </h4>
                            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 14px' }}>
                                Need: {selectedRequest.units_requested - (selectedRequest.units_issued || 0)} unit(s) · Selected: {selectedBags.length}
                            </p>
                            {inventory.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '30px 0', color: '#dc2626', fontSize: 13, fontWeight: 600 }}>
                                    No {selectedRequest.blood_group} blood available
                                </div>
                            ) : inventory.map(bag => {
                                const selected = selectedBags.find(b => b.id === bag.id);
                                const exp = new Date(bag.expiry_date);
                                const daysLeft = Math.ceil((exp - new Date()) / 86400000);
                                return (
                                    <div key={bag.id} onClick={() => toggleBag(bag)}
                                        style={{ padding: '12px 14px', borderRadius: 8, border: `2px solid ${selected ? '#059669' : '#e2e8f0'}`, marginBottom: 8, cursor: 'pointer', background: selected ? '#f0fdf4' : 'white' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ fontWeight: 700, fontSize: 13, fontFamily: 'monospace' }}>{bag.bag_number}</span>
                                            <span style={{ fontWeight: 900, color: GROUP_COLORS[bag.blood_group] }}>{bag.blood_group}</span>
                                        </div>
                                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                            {bag.component} · {bag.volume_ml}ml · Donor: {bag.donor_name || 'Unknown'}
                                        </div>
                                        <div style={{ fontSize: 11, marginTop: 4 }}>
                                            <span style={{ color: daysLeft <= 7 ? '#dc2626' : '#059669', fontWeight: 600 }}>
                                                Exp: {new Date(bag.expiry_date).toLocaleDateString('en-PK')} ({daysLeft}d left)
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}

                            {selectedBags.length > 0 && (
                                <button onClick={handleIssue} disabled={loading}
                                    style={{ width: '100%', marginTop: 14, padding: '12px', background: loading ? '#9ca3af' : '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                                    {loading ? 'Processing...' : `💉 Issue ${selectedBags.length} Unit(s) to ${selectedRequest.patient_name}`}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
